import React from 'react';
import { resolveLucideIcon, isRegistryKey } from './lucideRegistry';
import type { ExperienceIconKey } from './lucideRegistry';

export type ExperienceAccent =
	| 'blue'
	| 'purple'
	| 'violet'
	| 'teal'
	| 'green'
	| 'amber'
	| 'red'
	| 'neutral'
	| 'cyan';

export type TileIcon =
	| { kind: 'registry'; key: ExperienceIconKey; accent?: ExperienceAccent }
	| { kind: 'brand'; src?: string; alt?: string; initials?: string };

export interface ExperienceIconProps {
	/** Lucide registry key (e.g. "shield-check", "bot") */
	iconKey?: string | null;
	size?: number;
	strokeWidth?: number;
	className?: string;
	style?: React.CSSProperties;
	'aria-hidden'?: boolean;
}

export function ExperienceIcon({
	iconKey,
	size = 24,
	strokeWidth = 1.5,
	className,
	style,
	'aria-hidden': ariaHidden = true,
}: ExperienceIconProps) {
	const LucideComponent = resolveLucideIcon(iconKey);
	return (
		<LucideComponent
			size={size}
			strokeWidth={strokeWidth}
			className={className}
			style={style}
			aria-hidden={ariaHidden}
		/>
	);
}

const DEFAULT_CONTAINER_SIZE = 48;
const DEFAULT_ICON_SIZE = 26;

// Decorative category accents only. Status-semantic colors (amber, red, green)
// are intentionally NOT used as category defaults — they are reserved for
// genuine status via an explicit `accent` so a color never implies a warning
// where none exists (honest-status invariant). "Administration" was amber and
// falsely read as a warning state (worse in the light theme); it is neutral.
const GROUP_TO_ACCENT: Record<string, ExperienceAccent> = {
	'Administration': 'neutral',
	'AI & Automation': 'violet',
	'Identity': 'purple',
	'Applications': 'blue',
	'Analytics': 'cyan',
	'Extras': 'neutral',
	'Fun': 'neutral',
	'Productivity': 'teal',
	'Other': 'neutral',
};

export function resolveAccent(
	explicitAccent?: ExperienceAccent | null,
	group?: string | null,
): ExperienceAccent {
	if (explicitAccent) return explicitAccent;
	if (group && group in GROUP_TO_ACCENT) return GROUP_TO_ACCENT[group];
	return 'neutral';
}

function accentToCssVar(accent: ExperienceAccent): string {
	if (accent === 'violet') return 'var(--accent-violet, var(--agent-accent, var(--main-accent)))';
	return `var(--accent-${accent}, var(--main-accent))`;
}

export interface ExperienceIconContainerProps {
	iconKey?: string | null;
	accent?: ExperienceAccent | null;
	group?: string | null;
	containerSize?: number;
	iconSize?: number;
	className?: string;
}

export function ExperienceIconContainer({
	iconKey,
	accent: explicitAccent,
	group,
	containerSize = DEFAULT_CONTAINER_SIZE,
	iconSize = DEFAULT_ICON_SIZE,
	className,
}: ExperienceIconContainerProps) {
	const accent = resolveAccent(explicitAccent, group);
	const bgColor = accentToCssVar(accent);

	return (
		<div
			className={className}
			style={{
				width: containerSize,
				height: containerSize,
				borderRadius: 'var(--radius-md, 8px)',
				background: `color-mix(in srgb, ${bgColor} 18%, transparent)`,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
			}}
		>
			<ExperienceIcon
				iconKey={iconKey}
				size={iconSize}
				strokeWidth={1.5}
				style={{ color: bgColor }}
			/>
		</div>
	);
}

export function resolveTileIcon(
	manifestIcon?: string | null,
	group?: string | null,
	accent?: ExperienceAccent | null,
): TileIcon {
	if (manifestIcon && isRegistryKey(manifestIcon)) {
		return { kind: 'registry', key: manifestIcon, accent: resolveAccent(accent, group) };
	}
	return { kind: 'registry', key: 'app-window', accent: resolveAccent(accent, group) };
}

export { type ExperienceIconKey } from './lucideRegistry';
